import { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle } from 'lucide-react';
import type {
  DeliveryTicketExtraction,
  TicketPreviewPage,
} from '../../features/order-documents/api';
import {
  buildTicketApplyDraft,
  loadNumberMatches,
  ticketFieldStatus,
  type FieldStatus,
  type TicketApplyDraft,
} from '../../features/order-documents/deliveryTicket';
import {
  assignBoxToField,
  boxValue,
  scaleBox,
  type ApplyField,
} from '../../features/order-documents/ticketBoxes';

export interface DeliveryTicketReviewModalProps {
  extraction: DeliveryTicketExtraction;
  previewPages: TicketPreviewPage[];
  loadNumber: string | null;
  current: { quantity: number; ticketNumber: string; poNumber: string; deliveryDate: string };
  canEdit: boolean;
  onApply: (draft: TicketApplyDraft) => void;
  onClose: () => void;
}

const statusColor: Record<FieldStatus, string> = {
  match: '#16a34a',
  change: '#b45309',
  empty: '#94a3b8',
};

// review field -> the extraction box that field maps to
const FIELD_BOX: Record<ApplyField, string> = {
  quantity: 'netTons',
  ticketNumber: 'ticketNumber',
  poNumber: 'poNumber',
  deliveryDate: 'deliveryDate',
};

const ASSIGN_TARGETS: { key: ApplyField; label: string }[] = [
  { key: 'quantity', label: 'Delivered tons' },
  { key: 'ticketNumber', label: 'Ticket #' },
  { key: 'poNumber', label: 'PO #' },
  { key: 'deliveryDate', label: 'Delivery date' },
];

export default function DeliveryTicketReviewModal({
  extraction,
  previewPages,
  loadNumber,
  current,
  canEdit,
  onApply,
  onClose,
}: DeliveryTicketReviewModalProps) {
  const [draft, setDraft] = useState<TicketApplyDraft>(() => buildTicketApplyDraft(extraction));
  const [hovered, setHovered] = useState<string | null>(null); // a box.field
  const [assignBox, setAssignBox] = useState<number | null>(null); // index into page boxes
  const [pageIdx, setPageIdx] = useState(0);
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const page = previewPages[pageIdx] ?? previewPages[0];
  const pageBoxes = useMemo(
    () => (extraction.boxes ?? []).filter((b) => (Number(b.page) || 1) === (page?.page ?? 1)),
    [extraction.boxes, page]
  );

  const matches = loadNumberMatches(extraction, loadNumber);

  const onImgLoad = () => {
    const el = imgRef.current;
    if (el) setImgDims({ w: el.clientWidth, h: el.clientHeight });
  };

  const applyDraft = () => {
    onApply(draft);
    toast.info('Applied to the load — review the highlighted fields and Save.');
    onClose();
  };

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel" style={{ width: 'min(1100px, 96vw)' }}>
        <div className="modal__header">
          <h3 className="modal__title">Review delivery ticket</h3>
          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div
          className="modal__body"
          style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}
        >
          {/* Left: ticket image with overlay boxes */}
          <div style={{ flex: '1 1 380px', minWidth: 300 }}>
            {previewPages.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => setPageIdx((i) => Math.max(0, i - 1))}
                  disabled={pageIdx === 0}
                >
                  ‹ Prev
                </button>
                <span className="muted tiny">
                  Page {page?.page ?? 1} of {previewPages.length}
                </span>
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => setPageIdx((i) => Math.min(previewPages.length - 1, i + 1))}
                  disabled={pageIdx >= previewPages.length - 1}
                >
                  Next ›
                </button>
              </div>
            )}
            <div
              style={{
                position: 'relative',
                maxHeight: '70vh',
                overflow: 'auto',
                border: '1px solid #e2e8f0',
              }}
            >
              <img
                ref={imgRef}
                src={page?.url}
                alt="Delivery ticket"
                style={{ display: 'block', width: '100%' }}
                onLoad={onImgLoad}
              />
              {imgDims &&
                pageBoxes.map((box, i) => {
                  const r = scaleBox(box, imgDims.w, imgDims.h);
                  const active = hovered === box.field;
                  return (
                    <div
                      key={i}
                      onMouseEnter={() => setHovered(box.field)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => canEdit && setAssignBox(assignBox === i ? null : i)}
                      title={`${box.field}: ${boxValue(box, extraction)}`}
                      style={{
                        position: 'absolute',
                        left: r.left,
                        top: r.top,
                        width: r.width,
                        height: r.height,
                        border: `2px solid ${active ? '#2563eb' : 'rgba(37,99,235,0.5)'}`,
                        background: active ? 'rgba(37,99,235,0.18)' : 'rgba(37,99,235,0.06)',
                        borderRadius: 3,
                        cursor: canEdit ? 'pointer' : 'default',
                      }}
                    >
                      {assignBox === i && canEdit && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            zIndex: 5,
                            background: '#fff',
                            border: '1px solid #cbd5e1',
                            borderRadius: 6,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            padding: 4,
                            minWidth: 150,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="muted tiny" style={{ padding: '2px 6px' }}>
                            Assign “{boxValue(box, extraction) || '—'}” to:
                          </div>
                          {ASSIGN_TARGETS.map((t) => (
                            <button
                              key={t.key}
                              className="btn btn--ghost btn--sm"
                              style={{ display: 'block', width: '100%', textAlign: 'left' }}
                              onClick={() => {
                                setDraft((d) => assignBoxToField(d, box, extraction, t.key));
                                setAssignBox(null);
                              }}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            <div className="muted tiny" style={{ marginTop: 4 }}>
              Hover a field to find it on the ticket. Click a box to assign its value to a field.
            </div>
          </div>

          {/* Right: editable review */}
          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            {!matches && (
              <div
                style={{
                  marginBottom: 8,
                  padding: '8px 10px',
                  borderRadius: 6,
                  background: '#fef2f2',
                  border: '1px solid #ef4444',
                  color: '#991b1b',
                  fontSize: 12,
                }}
              >
                <AlertTriangle size={13} /> This ticket's load number{' '}
                <strong>{extraction.loadNumber || '(none)'}</strong> does not match this load{' '}
                <strong>{loadNumber || '(none)'}</strong>.
              </div>
            )}

            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                <Row
                  field="quantity"
                  label="Delivered tons"
                  value={draft.quantity}
                  current={String(current.quantity ?? '')}
                  canEdit={canEdit}
                  hovered={hovered}
                  setHovered={setHovered}
                  onChange={(v) => setDraft({ ...draft, quantity: v })}
                />
                <Row
                  field="ticketNumber"
                  label="Ticket #"
                  value={draft.ticketNumber}
                  current={current.ticketNumber}
                  canEdit={canEdit}
                  hovered={hovered}
                  setHovered={setHovered}
                  onChange={(v) => setDraft({ ...draft, ticketNumber: v })}
                />
                <Row
                  field="poNumber"
                  label="PO #"
                  value={draft.poNumber}
                  current={current.poNumber}
                  canEdit={canEdit}
                  hovered={hovered}
                  setHovered={setHovered}
                  onChange={(v) => setDraft({ ...draft, poNumber: v })}
                />
                <Row
                  field="deliveryDate"
                  label="Delivery date"
                  value={draft.deliveryDate}
                  current={current.deliveryDate}
                  canEdit={canEdit}
                  hovered={hovered}
                  setHovered={setHovered}
                  onChange={(v) => setDraft({ ...draft, deliveryDate: v })}
                />
                <ReadonlyRow label="Product" value={extraction.productName} />
                <ReadonlyRow label="Carrier" value={extraction.carrierName} />
                <ReadonlyRow label="Weighmaster" value={extraction.weighmaster} />
              </tbody>
            </table>

            {canEdit && (
              <div style={{ marginTop: 12 }}>
                <button className="btn btn--primary" onClick={applyDraft}>
                  Apply to load
                </button>
                <span className="muted tiny" style={{ marginLeft: 8 }}>
                  then Save to persist.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row(props: {
  field: ApplyField;
  label: string;
  value: string;
  current: string;
  canEdit: boolean;
  hovered: string | null;
  setHovered: (f: string | null) => void;
  onChange: (v: string) => void;
}) {
  const s = ticketFieldStatus(props.value, props.current);
  const boxField = FIELD_BOX[props.field];
  const active = props.hovered === boxField;
  return (
    <tr
      onMouseEnter={() => props.setHovered(boxField)}
      onMouseLeave={() => props.setHovered(null)}
      style={{ background: active ? 'rgba(37,99,235,0.08)' : undefined }}
    >
      <td style={{ padding: '4px 8px 4px 4px', color: '#475569', whiteSpace: 'nowrap' }}>
        {props.label}
      </td>
      <td style={{ padding: '4px 0' }}>
        <input
          className="input-lg"
          value={props.value}
          disabled={!props.canEdit}
          onChange={(e) => props.onChange(e.target.value)}
          style={{ width: '100%', borderColor: s === 'change' ? '#f59e0b' : undefined }}
        />
      </td>
      <td
        style={{
          padding: '4px 0 4px 8px',
          color: statusColor[s],
          whiteSpace: 'nowrap',
          fontSize: 12,
        }}
      >
        {s === 'match' && '✓ matches'}
        {s === 'change' && `was ${props.current || '—'}`}
        {s === 'empty' && 'not on ticket'}
      </td>
    </tr>
  );
}

function ReadonlyRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <tr>
      <td style={{ padding: '4px 8px 4px 4px', color: '#475569', whiteSpace: 'nowrap' }}>
        {label}
      </td>
      <td colSpan={2} style={{ padding: '4px 0' }}>
        {value}
      </td>
    </tr>
  );
}
